import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppPartnerService, EditionUpgradeAction, UpgradeDialogContent } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  AppPartnerSettingsService,
  Currency,
  Frequency,
  MarketplacePackagesApiService,
  Package,
  Price,
  RetailPrice,
  getCurrencyCode,
} from '@vendasta/marketplace-packages';
import { Status as SalesOrderStatus, SalesOrdersService } from '@vendasta/sales-orders';
import { DisplayPriceService } from '@vendasta/shared';
import { UpgradeCTADialogComponent, UpgradeCTADialogConfig, UpgradeCTADialogResult } from '@vendasta/uikit';
import { EMPTY, combineLatest, of } from 'rxjs';
import { catchError, filter, flatMap, map, switchMap } from 'rxjs/operators';
import { Edition } from '../../common/editions';
import { AppConfigService } from '../../core/app-config.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { ProApiService } from '../../core/pro.api.service';

const PRICING_FREQUENCY_ORDER_ASC = [
  Frequency.DAILY,
  Frequency.WEEKLY,
  Frequency.MONTHLY,
  Frequency.YEARLY,
  Frequency.ONCE,
  Frequency.OTHER,
];
const FREQUENCY_TO_TRANSLATION_KEY = new Map([
  [Frequency.DAILY, 'DISPLAY_PRICE_FREQUENCY.DAY'],
  [Frequency.WEEKLY, 'DISPLAY_PRICE_FREQUENCY.WEEK'],
  [Frequency.MONTHLY, 'DISPLAY_PRICE_FREQUENCY.MONTH'],
  [Frequency.YEARLY, 'DISPLAY_PRICE_FREQUENCY.YEAR'],
  [Frequency.ONCE, 'DISPLAY_PRICE_FREQUENCY.ONE_TIME'],
  [Frequency.OTHER, 'DISPLAY_PRICE_FREQUENCY.OTHER'],
]);
const CONTACT_SALES = -1;

@Injectable()
export class ExpressCtaDialogService {
  private _retailPrice: string;
  private _retailFrequency: string;
  private _orderConfirmationUrl: string;
  private _salesOrderExists: boolean;
  private _dialogContent: UpgradeDialogContent;
  private _editionChangeAction: EditionUpgradeAction;
  private _customPackageUpgradeText: string;
  private _customPackagePrice: string;
  private _customPackagePriceFrequency: string;
  private _customPathHasMultiplePackages: boolean;

  dialogRef: MatDialogRef<UpgradeCTADialogComponent, UpgradeCTADialogResult>;

  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private appConfigService: AppConfigService,
    private customerVoiceRouter: CustomerVoiceRouterService,
    private partnerSettingsService: AppPartnerSettingsService,
    private proApiService: ProApiService,
    private snackbarService: SnackbarService,
    private salesOrdersService: SalesOrdersService,
    private displayPriceService: DisplayPriceService,
    private appPartnerService: AppPartnerService,
    private packageService: MarketplacePackagesApiService,
  ) {}

  open(imageUrl: string, imageText: string, clickUpgradeLabel: string, keySellingPoints?: string[]): void {
    if (!this.dialogRef) {
      let upgradeNowText = null;
      let price = '';
      let frequency: string = null;

      if (this._editionChangeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CREATE_ORDER) {
        upgradeNowText = this.translateService.instant('EXPRESS_CTA_DIALOG.UPGRADE_NOW_TEXT');
        price = this._retailPrice || '';
        frequency = this._retailFrequency || null;
      } else if (this._editionChangeAction === EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER) {
        upgradeNowText = this._customPackageUpgradeText;
        price = this._customPackagePrice;
        frequency = this._customPackagePriceFrequency
          ? this.translateService.instant(this._customPackagePriceFrequency)
          : null;
      }
      const dialogConfig: UpgradeCTADialogConfig = {
        imageUrl: (!!this._dialogContent && this._dialogContent.imageUrl) || imageUrl,
        imageText: (!!this._dialogContent && this._dialogContent.titleText) || this.translateService.instant(imageText),
        keySellingPoints: (!!this._dialogContent && this._dialogContent.sellingPoints) ||
          keySellingPoints || [
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.UNLIMITED_EMAIL'),
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.SMS_ADDONS'),
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.40_TEMPLATES'),
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.MORE_REVIEW_SITES'),
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.FOLLOW_UP_EMAILS'),
            this.translateService.instant('EXPRESS_CTA_DIALOG.KEY_SELLING_POINTS.BULK_SEND'),
          ],
        upgradeNowText: upgradeNowText,
        price: price,
        frequency: frequency,
        editionName: this.translateService.instant('EXPRESS_CTA_DIALOG.EDITION_NAME'),
        ctaText:
          (!!this._dialogContent && this._dialogContent.buttonText) ||
          this.translateService.instant('EXPRESS_CTA_DIALOG.CTA_TEXT'),
      };

      dialogConfig.activeOrderPrimaryText = this.translateService.instant('EXPRESS_CTA_DIALOG.ACTIVE_ORDER.HOLD_TIGHT');
      dialogConfig.activeOrderSecondaryText = this.translateService.instant(
        'EXPRESS_CTA_DIALOG.ACTIVE_ORDER.ON_THE_WAY',
      );
      dialogConfig.activeOrderImageUrl =
        'https://vstatic-prod.apigateway.co/customer-voice-client.9223372035073077600.prod/assets/images/express_cta/active_order.png';

      this.dialogRef = this.dialog.open(UpgradeCTADialogComponent, {
        panelClass: 'cv-upgrade-dialog',
        data: dialogConfig,
      });
      this.dialogRef.componentInstance.hasActiveOrder = this._salesOrderExists;

      combineLatest([this.dialogRef.afterClosed(), this.appConfigService.appConfig]).subscribe(
        ([result, config]) => {
          this.dialogRef = null;
          if (result?.confirmed) {
            if (config.isSMB) {
              window.location.href = `${this._orderConfirmationUrl}&trackingCode=cv-${clickUpgradeLabel}`;
            } else {
              this.snackbarService.errorSnack(
                this.translateService.instant('EXPRESS_CTA_DIALOG.IMPERSONATION_FORBIDDEN'),
              );
            }
          }
        },
      );
    }
  }

  loadRetailPrice(): void {
    combineLatest([this.appConfigService.appConfig, this.customerVoiceRouter.accountGroupId])
      .pipe(
        filter(([appConfig]) => appConfig.edition === Edition.Express),
        switchMap(([appConfig, agid]) =>
          combineLatest([
            this.partnerSettingsService
              .getRetailPrice(appConfig.vendorCenterAppId, agid, null, true)
              .pipe(catchError(() => of(null))),
            this.partnerSettingsService
              .getRetailPrice(appConfig.vendorCenterAppId, agid, null, false)
              .pipe(catchError(() => of(null))),
          ]),
        ),
        map(([overridePrice, defaultPrice]) => overridePrice || defaultPrice),
        switchMap((retailPrice: RetailPrice) => {
          const currencyKey: string = Currency[retailPrice.currency || Currency.USD];
          const frequencyKey: Frequency = retailPrice.billingFrequency || Frequency.MONTHLY;
          this._retailFrequency = FREQUENCY_TO_TRANSLATION_KEY.get(frequencyKey);
          return this.displayPriceService.formatDisplayPrice(
            retailPrice.price,
            currencyKey,
            null,
            true,
            true,
            true,
            false,
          );
        }),
        catchError(() => {
          this._retailPrice = null;
          return EMPTY;
        }),
      )
      .subscribe((priceString) => {
        this._retailPrice = priceString;
      });
  }

  loadOrderConfirmationUrl(): void {
    combineLatest([this.appConfigService.appConfig, this.customerVoiceRouter.accountGroupId])
      .pipe(
        filter(([appConfig]) => appConfig.edition === Edition.Express),
        flatMap(([appConfig, agid]) => {
          return this.proApiService.getVbcHostname(agid).pipe(
            map((host) => {
              const items = encodeURIComponent(JSON.stringify([{ appId: appConfig.vendorCenterAppId, editionId: '' }]));
              const nextUrl = encodeURIComponent(window.location.href);
              return `${host}/account/location/${agid}/edition-upgrades?items=${items}&nextUrl=${nextUrl}`;
            }),
          );
        }),
      )
      .subscribe((url) => {
        this._orderConfirmationUrl = url;
      });
  }

  loadDialogContent(): void {
    this.appConfigService.appConfig
      .pipe(
        switchMap((config) => {
          return this.appPartnerService.getUpgradeDialogContent(
            config.partnerId,
            config.marketId,
            config.vendorCenterAppId,
          );
        }),
      )
      .subscribe((response) => (this._dialogContent = response.upgradeDialogContent));
  }

  loadAppSettings(): void {
    this.appConfigService.appConfig
      .pipe(
        switchMap((config) => {
          return this.appPartnerService.getAppSettings(config.vendorCenterAppId, config.partnerId, config.marketId);
        }),
        flatMap((settings) => {
          if (settings.editionChange?.priceReferencePackageId) {
            const priceDetails$ = this.packageService
              .getMultiPackages({
                packageIds: [settings.editionChange.priceReferencePackageId],
              })
              .pipe(
                map((customPackage) => {
                  return this.getMostFrequentPriceAndFrequency(customPackage.packageContainers[0].package);
                }),
              );
            const priceResult$ = priceDetails$.pipe(
              switchMap((priceDetails) => {
                return this.displayPriceService.formatDisplayPrice(
                  priceDetails.price.value,
                  priceDetails.price.currencyCode,
                  null,
                  true,
                  true,
                  true,
                  false,
                );
              }),
            );
            return combineLatest([priceDetails$, priceResult$]).pipe(
              map(([priceDetails, displayPrice]) => {
                const hasCustomUpgradePath: boolean =
                  settings.editionChange.editionUpgradeAction ===
                  EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER;
                const hasMultipleUpgradePackages: boolean = settings.editionChange.customUpgradePackageIds.length > 1;
                let upgradeText: string =
                  !hasCustomUpgradePath || !hasMultipleUpgradePackages
                    ? this.translateService.instant('EXPRESS_CTA_DIALOG.UPGRADE_NOW_TEXT')
                    : this.translateService.instant('EXPRESS_CTA_DIALOG.UPGRADE_STARTING_AT_TEXT');
                let frequencyKey: string = priceDetails.frequency;
                if (priceDetails.price.value === undefined || priceDetails.price.value === 0) {
                  // The price is `free`.
                  frequencyKey = null;
                } else if (priceDetails.price.value === null || priceDetails.price.value < 0) {
                  // The price is `contact sales`.
                  upgradeText = null;
                  frequencyKey = null;
                }
                return {
                  editionChangeAction: settings.editionChange.editionUpgradeAction,
                  customPackageUpgradeText: upgradeText,
                  customPackagePrice: displayPrice,
                  customPackagePriceFrequency: frequencyKey,
                  customPathHasMultiplePackages: settings.editionChange.customUpgradePackageIds.length > 1,
                };
              }),
            );
          }
          return of({
            editionChangeAction:
              settings.editionChange?.editionUpgradeAction || EditionUpgradeAction.EDITION_UPGRADE_ACTION_CREATE_ORDER,
            customPackageUpgradeText: null,
            customPackagePrice: '',
            customPackagePriceFrequency: '',
            customPathHasMultiplePackages: settings.editionChange?.customUpgradePackageIds?.length > 1,
          });
        }),
      )
      .subscribe(
        (response) => {
          this._editionChangeAction = response.editionChangeAction;
          this._customPackageUpgradeText = response.customPackageUpgradeText;
          this._customPackagePrice = response.customPackagePrice;
          this._customPackagePriceFrequency = response.customPackagePriceFrequency;
          this._customPathHasMultiplePackages = response.customPathHasMultiplePackages;
        },
        () => {
          this._editionChangeAction = null;
          this._customPackageUpgradeText = null;
          this._customPackagePrice = '';
          this._customPackagePriceFrequency = '';
          this._customPathHasMultiplePackages = false;
        },
      );
  }

  getMostFrequentPriceAndFrequency(pack: Package): {
    price: { value: number; currencyCode: string };
    frequency: string;
  } {
    let mostFrequentPrice: Price;
    if (!!pack && !!pack.pricing && !!pack.pricing.prices) {
      pack.pricing.prices.forEach((price) => {
        if (price.price === null) {
          price.price = CONTACT_SALES;
        } else if (price.price === undefined) {
          price.price = 0;
        }
        if (!price.frequency) {
          price.frequency = Frequency.MONTHLY;
        }
        if (!mostFrequentPrice) {
          mostFrequentPrice = price;
          return;
        }

        const highestFrequency = this.normalizeFrequencyEnum(mostFrequentPrice.frequency);
        const currentFrequency = this.normalizeFrequencyEnum(price.frequency);

        if (
          (PRICING_FREQUENCY_ORDER_ASC.indexOf(currentFrequency) <
            PRICING_FREQUENCY_ORDER_ASC.indexOf(highestFrequency) &&
            price.price > 0) ||
          (PRICING_FREQUENCY_ORDER_ASC.indexOf(currentFrequency) ===
            PRICING_FREQUENCY_ORDER_ASC.indexOf(highestFrequency) &&
            price.price > 0 &&
            (price.price < mostFrequentPrice.price || mostFrequentPrice.price <= 0))
        ) {
          mostFrequentPrice = price;
        }
      });
    }

    const priceValue = mostFrequentPrice.price;
    const currencyCode = pack.pricing
      ? getCurrencyCode(pack.pricing.currency, Currency[Currency.USD])
      : Currency[Currency.USD];
    const frequencyTranslationKey =
      !!mostFrequentPrice && !!mostFrequentPrice.frequency
        ? FREQUENCY_TO_TRANSLATION_KEY.get(mostFrequentPrice.frequency)
        : FREQUENCY_TO_TRANSLATION_KEY.get(Frequency.MONTHLY);
    return {
      price: { value: priceValue, currencyCode: currencyCode },
      frequency: frequencyTranslationKey,
    };
  }

  private normalizeFrequencyEnum(value: keyof typeof Frequency | Frequency): Frequency {
    if (typeof value === 'string') {
      return Frequency[value];
    }

    return value;
  }

  checkIfSalesOrderExists(): void {
    combineLatest([this.appConfigService.appConfig, this.customerVoiceRouter.accountGroupId])
      .pipe(
        filter(([appConfig]) => appConfig.edition === Edition.Express),
        flatMap(([appConfig, agid]) => {
          const statuses: SalesOrderStatus[] = [
            SalesOrderStatus.SUBMITTED,
            SalesOrderStatus.APPROVED,
            SalesOrderStatus.PROCESSING,
          ];
          return this.salesOrdersService.check(
            appConfig.partnerId,
            agid,
            appConfig.vendorCenterAppId,
            appConfig.editionIds.express,
            statuses,
          );
        }),
      )
      .subscribe(
        (response) => {
          this._salesOrderExists = response ? response.valueOf() : false;
        },
        () => {
          this._salesOrderExists = false;
        },
      );
  }
}
